import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FormikProps, useFormik } from "formik";
import { ModalComponent } from "..";
import {
  deepCopy,
  moveElement,
  removeElementAtIndex,
} from "../../../helperFunctions";
import { useUpdateCooperativeApprovalChainMutation } from "../../../redux/features/approvals/approvals.api";
import {
  CooperativeAction,
  SingleApprovalChain,
} from "../../../redux/features/approvals/approvals.type";
import { useGetCooperativeRolesQuery } from "../../../redux/features/settings/settings.api";
import { SingleRole } from "../../../redux/features/settings/settings.type";
import { approvalChainsMap } from "../../../utils/constants";
import { editApprovalChainSchema } from "../../../validationSchema";
import { Button } from "../../buttons/button";
import { Icon } from "../../icons/icon";
import ComboBox from "../../inputs/combobox";
import { doNotification } from "../../toast/nofitication";

type FormPayload = {
  chain: {
    roleId: string;
    roleName: string;
  }[];
};

const ApprovalLevelSelector = ({
  id,
  formik,
  roles,
  idx,
  approvalStep,
  fetchingRoles,
}: {
  id: string;
  formik: FormikProps<FormPayload>;
  roles: SingleRole[];
  idx: number;
  approvalStep: {
    roleId: string;
    roleName: string;
  };
  fetchingRoles: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { values, errors, handleBlur, handleChange, setFieldValue } = formik;

  const alreadySelectedRoles = formik.values.chain.map((c) => c.roleId);

  return (
    <div className="flex items-start gap-2" ref={setNodeRef} style={style}>
      <button
        {...attributes}
        {...listeners}
        className="w-fit h-[48px] mt-[25px] text-center px-[6px]"
        type="button"
      >
        <Icon svg="grip" width={20} height={20} className="cursor-grab" />
      </button>

      <ComboBox
        type="text"
        name={`values[${idx}].roleId`}
        value={approvalStep.roleName}
        placeholder="Select Role"
        onChange={handleChange}
        onBlur={handleBlur}
        hasIcon={false}
        iconSvg={undefined}
        errors={
          errors?.chain && (errors?.chain[idx] as any)?.roleId
            ? (errors?.chain[idx] as any)?.roleId
            : ""
        }
        title={`Level ${idx + 1}`}
        hasTitle
        autoComplete="off"
        showEmptyState={true}
        menuItems={
          roles
            .filter((r) => {
              return !alreadySelectedRoles.includes(`${r.id}`);
            })
            .map((role) => {
              return {
                name: role.Name,
                value: {
                  roleId: `${role.id}`,
                  roleName: `${role.Name}`,
                  rolePermissions: role.permissions.map((p) => p.CodeName),
                },
              };
            }) ?? []
        }
        onSelectOption={(val) => {
          setFieldValue(`chain[${idx}]`, val);
        }}
        loading={fetchingRoles}
        useInputLoader
        menuClassName="!z-[999]"
        immediate
        isDropdown
        className="flex-1"
        containerClassName="flex-1"
        position="bottom start"
      />

      {values.chain.length <= 2 ? undefined : (
        <button
          className="h-[48px] mt-[25px] w-fit bg-[#FDF7F7] text-red-200 px-[6px] border border-pry-dark-green-300/10 rounded-[4px] disabled:cursor-not-allowed"
          disabled={values.chain.length <= 2}
          onClick={() => {
            setFieldValue(`chain`, removeElementAtIndex(values.chain, idx));
          }}
        >
          <Icon svg="delete-icon" width={20} height={20} />
        </button>
      )}
    </div>
  );
};

export const ManageApprovalChainModal = ({
  isModalOpen,
  onModalClose,
  successCallback = () => {},
  approvalChain,
  action,
}: {
  isModalOpen: boolean;
  onModalClose: () => void;
  successCallback?: () => void;
  approvalChain: SingleApprovalChain[];
  action: CooperativeAction;
}) => {
  const [editChainConfiguration, { isLoading: editingChainConfiguration }] =
    useUpdateCooperativeApprovalChainMutation();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Start dragging after moving 5px
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // Press and hold delay for touch
        tolerance: 1,
      },
    }),
    useSensor(MouseSensor)
  );

  const { data: roles, isFetching: fetchingRoles } =
    useGetCooperativeRolesQuery(
      {
        limit: 100,
        offset: 0,
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const formik = useFormik({
    initialValues: {
      chain: approvalChain.map((v) => {
        return {
          roleId: `${v.Role.id}`,
          roleName: `${v.Role.Name}`,
          rolePermissions:
            roles?.data?.results
              ?.find((r) => r.id === v.Role.id)
              ?.permissions?.map((p) => p.CodeName) ?? [],
        };
      }),
    } as FormPayload,
    enableReinitialize: true,
    validationSchema: editApprovalChainSchema(approvalChainsMap[action]),
    onSubmit: async (data) => {
      const resp = await editChainConfiguration({
        codeAction: action,
        approval: data.chain.map((v, idx) => ({
          level: idx + 1,
          roleId: v.roleId,
        })),
      });

      if (resp.data?.success) {
        doNotification(
          `${approvalChainsMap[action].title} saved successfully`,
          "success"
        );
        formik.resetForm();
        onModalClose();
        successCallback();
      }
    },
  });

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;

    if (over && active.id !== over.id) {
      const chainCopy = deepCopy(formik.values.chain);
      const chainCopyMod = chainCopy.map((c) => {
        return {
          id: `${c.roleId}-${c.roleName}`,
          ...c,
        };
      });

      const oldIndex = chainCopyMod.findIndex((item) => item.id === active.id);
      const newIndex = chainCopyMod.findIndex((item) => item.id === over.id);

      formik.setFieldValue("chain", moveElement(chainCopy, oldIndex, newIndex));
    }
  };

  return (
    <>
      <ModalComponent
        isModalOpen={isModalOpen}
        onModalClose={() => {
          formik.resetForm();
          onModalClose();
        }}
        title={approvalChainsMap[action].title}
        subTitle={approvalChainsMap[action].subtext}
        childrenClassName=""
        modalClassName=""
        hasButtons
        proceedButtonText={"Save Changes"}
        onProceedClick={() => {
          formik.validateForm();
          formik.handleSubmit();
        }}
        hasCancelButton={false}
        isLoading={editingChainConfiguration}
        isProceedDisabled={
          editingChainConfiguration || !formik.isValid || fetchingRoles
        }
      >
        <div className="flex flex-col gap-4">
          <DndContext
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
            sensors={sensors}
          >
            <SortableContext
              items={formik.values.chain.map((c) => {
                return {
                  id: `${c.roleId}-${c.roleName}`,
                  ...c,
                };
              })}
            >
              {formik.values.chain.map((approvalStep, idx) => {
                return (
                  <ApprovalLevelSelector
                    key={`${idx}`}
                    id={`${approvalStep.roleId}-${approvalStep.roleName}`}
                    idx={idx}
                    formik={formik}
                    roles={roles?.data?.results ?? []}
                    fetchingRoles={fetchingRoles}
                    approvalStep={approvalStep}
                  />
                );
              })}
            </SortableContext>
          </DndContext>

          <Button
            type="button"
            variation="secondary"
            size="medium"
            className="!rounded-full !ml-auto !w-fit"
            icon="plus"
            text="Add approval level"
            onClick={() => {
              formik.setFieldValue(`chain`, [...formik.values.chain, {}]);
            }}
          />
        </div>
      </ModalComponent>
    </>
  );
};
